import type { BrandFragment } from "@shared/data/fragments/BrandFragment/BrandFragment.fragment";
import type { EqpLevelType } from "@shared/types/graphqlGenerated";

type Brand = Pick<BrandFragment, "id" | "name" | "logo">;

// used to record where the search session started from
// for special catalog behaviors like hiding brands filters
export enum SearchEntrypoints {
  MEGAMENU_BRANDS = "megamenu_brands",
  TYPEAHEAD_BRANDS = "typeahead_brands",
  CATEGORY_PAGE_BRANDS = "category_page_brands",
  OTHER = "other",
}
export type SearchEntrypoint =
  | SearchEntrypoints.MEGAMENU_BRANDS
  | SearchEntrypoints.TYPEAHEAD_BRANDS
  | SearchEntrypoints.CATEGORY_PAGE_BRANDS
  | SearchEntrypoints.OTHER;

interface CatalogSearchConfigParams {
  sortDirection?: ProductSortDirection;
  sortField?: ProductSortField;
  isOnlyShowFavorites?: boolean;
  isOnlyShowInStock?: boolean;
  isNew?: boolean;
  isFsTested?: boolean;
}

interface CatalogSearchableParams {
  allergens?: Filter[];
  brands?: Brand[];
  supplementTypes?: SupplementTypeFilter[];
  conditions?: ConditionFilter[];
  categories?: CategoryFilter[];
  certifications?: Filter[];
  childTags?: string[];
  forms?: Filter[];
  ingredients?: IngredientFilterV2[];
  excludedIngredients?: IngredientFilter[];
  populations?: Filter[];
  query?: string;
  tags?: string[];
  more?: Filter[];
  onlyIngredient?: boolean;
  typeaheadSelectionId?: string;
  relevanceFilters?: RelevanceFilter[];
  eqpLevels?: EqpLevelType[];
  taxonomies?: TaxonomyFilter[];
  optimizedTaxonomies?: OptimizedTaxonomyFilter[];
  catalogSearchType?: CatalogSearchType;
}

interface CatalogProductParams {
  variantId?: string;
}

interface CatalogViewParams {
  gridView?: boolean;
}

interface CatalogEntrypointParams {
  searchSessionEntrypoint?: SearchEntrypoint;
}
type CatalogSearchParams = CatalogSearchableParams &
  CatalogSearchConfigParams &
  CatalogProductParams &
  CatalogViewParams &
  CatalogEntrypointParams;

interface CatalogParams extends CatalogSearchParams {
  favorite?: string;
  swap?: string;
}

interface CatalogProductParams {
  variantId?: string;
}

interface IngredientFilter {
  name: string;
  searchAttributes: IngredientSearchAttributes;
}

interface IngredientFilterV2 {
  name: string;
  searchAttributes: IngredientSearchAttributesV2;
}

interface SupplementTypeFilter {
  id: string;
  name: string;
}

interface ConditionFilter {
  id: string;
  name: string;
}

interface CategoryFilter {
  id: string;
  name: string;
}

interface TaxonomyFilter {
  id: string;
  name: string;
}

/**
 * hideFromDisplay: true/false
 * If true, the pill tag will not be displayed in the UI
 *
 *
 * type: "search"
 * These are the original taxonomies selected from the mega menu dropdown
 * or typeahead that determines how the taxonomy tree in the category filter
 * is to be rendered.
 *
 * It is kept in the url to ensure upon reload the taxonomy tree in the
 * category filter is rendered the same.
 *
 * It's not send to the BE as part of the search query. It is only used
 * on the FE.
 *
 * It's also used to instruct the FE to clear all prior search params.
 *
 *
 * type: "filter"
 * These are taxonomies that are checked in the category filter, they
 * are sent to the BE as part of the search query.
 *
 *
 * type: "lock"
 * If user begins a search by selecting a taxonomy, and proceeds to clear
 * all filters, a "lock" taxonomy is added to ensure the search
 * still remains within the taxonomy region. They are send to the BE
 * as part of the search query.
 *
 * The moment a user re-selects a taxonomy from the category filter, the
 * "lock" taxonomy is removed.
 */
interface OptimizedTaxonomyFilter {
  id: string;
  name: string;
  hideFromDisplay?: boolean;
  type: "search" | "filter" | "lock";
}

interface RelevanceFilter {
  id: string;
  category: RelevanceFilterCategories;
  query?: string;
}

type RelevanceFilterTypes =
  | Brand[]
  | IngredientFilter[]
  | IngredientFilterV2[]
  | ConditionFilter[]
  | SupplementTypeFilter[];

type CatalogSearchType = "brands" | "filters" | "";

type SearchFilterCategories =
  | "Form"
  | "Allergens"
  | "Certifications"
  | "Brand"
  | "SupplementType"
  | "Condition"
  | "Category"
  | "Taxonomy"
  | "OptimizedTaxonomy"
  | "Text"
  | "More"
  | "Demographic"
  | "InStock"
  | "Favorite"
  | "EqpLevels"
  | "FullscriptTested";

type RelevanceFilterCategories = "Brand" | "Ingredient" | "Condition" | "SupplementType";

type Filter = {
  category: SearchFilterCategories;
  name: string;
  id: string;
};

interface ContextualFilter extends Filter {
  esResultCount: number;
}

type ProductSortField = "ALPHABETICAL" | "PRICE" | "RELEVANCE" | "MOST_PRESCRIBED";

type ProductSortDirection = "ASC" | "DESC";
type IngredientDoseSymbol = "lte" | "gte" | "eq";

type IngredientUnit = { unitOne: string } | { unitTwo: string };

interface IngredientSearchAttributes {
  id: string;
  addon?: {
    dose: number;
    symbol: IngredientDoseSymbol;
    unit: IngredientUnit;
  };
}

interface IngredientSearchAttributesV2 {
  id: string;
  addon?: {
    minDose?: number;
    maxDose?: number;
    unit?: IngredientUnit;
  };
}

interface SortAttributes {
  field: ProductSortField;
  direction: ProductSortDirection;
}

interface ProductSearchParams {
  query?: string;
  typeaheadSelectionId?: string;
  sort?: SortAttributes[];
  filters?: {
    onlyShowFavorites?: boolean;
    brandIds?: string[];
    brandSlugs?: string[];
    supplementTypeIds?: string[];
    conditionIds?: string[];
    categoryIds?: string[];
    taxonomyIds?: string[];
    optimizedTaxonomyIds?: string[];
    allergenIds?: string[];
    populationIds?: string[];
    thirdPartyCertificationIds?: string[];
    baseDosageFormatIds?: string[];
    ingredients?: IngredientSearchAttributes[];
    excludedIngredients?: IngredientSearchAttributes[];
    inStockOnly?: boolean;
    eeNew?: boolean;
    onlyIngredient?: boolean;
    onlyWholesaleHistory?: boolean;
    eqpLevels?: EqpLevelType[];
    sku?: string;
    emersonVariantId?: string;
    isFsTested?: boolean;
  };
}

interface IngredientSelectionParams {
  ingredientSelected: IngredientFilter;
  symbol: IngredientDoseSymbol;
  dosage: number;
  unit: IngredientUnit;
  subTypeSelected: IngredientFilter;
}

interface IngredientSelectionParamsV2 {
  ingredientSelected: IngredientFilterV2;
  minDose?: number;
  maxDose?: number;
  unit: IngredientUnit;
}

export type {
  Brand,
  CatalogParams,
  CatalogSearchType,
  CategoryFilter,
  ConditionFilter,
  Filter,
  ContextualFilter,
  IngredientFilter,
  IngredientFilterV2,
  IngredientSelectionParams,
  IngredientSelectionParamsV2,
  IngredientUnit,
  ProductSearchParams,
  ProductSortDirection,
  ProductSortField,
  RelevanceFilter,
  RelevanceFilterCategories,
  RelevanceFilterTypes,
  SearchFilterCategories,
  SupplementTypeFilter,
  TaxonomyFilter,
  OptimizedTaxonomyFilter,
};
